import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { isEmpty, isNil } from "ramda";
import { styled } from "styled-components";

import { InputField } from "affinityForm/fields";
import { Button } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { Layout } from "affinityUI/structures/layout";
import { useAuth } from "lib/auth";
import { getExhClient, getStoredRefreshToken } from "lib/extrahorizon/client";
import { Yup } from "lib/yup/extendYup";

import { Error, ErrorPanel } from "../components/styled";

const loginValidationSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const LoginMetaGrid = styled(Grid)`
  margin-top: -1rem;
`;

const getCurrentURL = () => {
  return window.location.href;
};

const getCurrentURLOrigin = () => {
  return new URL(getCurrentURL()).origin;
};

const isRelativeURL = (targetURL: string | URL) =>
  getCurrentURLOrigin() === new URL(targetURL, getCurrentURLOrigin()).origin;

const isExternalURL = (targetURL: string | URL) => new URL(targetURL).origin !== getCurrentURLOrigin();

const isTargetExternalURL = (targetURL: string | URL) => !isRelativeURL(targetURL) && isExternalURL(targetURL);

export const LoginPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const target = searchParams.get("target");
  const navigate = useNavigate();
  const [, { currentUser }] = useAuth();

  // If already logged in, redirect to target or home
  useEffect(() => {
    if (currentUser) {
      const navUrl =
        isNil(target) || isEmpty(target) || isTargetExternalURL(target)
          ? "/"
          : target.startsWith("/")
            ? target
            : new URL(target).pathname;
      navigate(navUrl, {
        replace: true,
      });
    }
  }, [currentUser, target, navigate]);

  const handleSubmit = useCallback(
    async (data: { username: string; password: string }) => {
      setError(null);
      setIsLoading(true);

      try {
        // Use the global ExtraHorizon client (initialized in index.tsx)
        const exh = getExhClient();

        // eslint-disable-next-line no-console
        console.log("Attempting authentication...");

        // Authenticate with username and password via proxy
        await exh.auth.authenticate({
          username: data.username,
          password: data.password,
        });

        // Verify refresh token was saved
        const savedToken = getStoredRefreshToken();
        // eslint-disable-next-line no-console
        console.log("Authentication successful! Refresh token saved:", Boolean(savedToken));

        // Navigate to target or home page
        const navUrl =
          isNil(target) || isEmpty(target) || isTargetExternalURL(target)
            ? "/"
            : target.startsWith("/")
              ? target
              : new URL(target).pathname;

        // Reload the page to reinitialize auth with new session
        // The global client will have the tokens persisted
        window.location.href = navUrl;
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error("Login error:", err);

        let errorMessage = "Email or password is not correct";

        // Check if error has a response with status code
        if (err && typeof err === "object") {
          const errorObj = err as Record<string, unknown>;

          // Check for HTTP status codes
          if ("status" in errorObj) {
            const status = errorObj.status as number;
            if (status === 400 || status === 401) {
              errorMessage = "Email or password is not correct";
            } else if (status === 403) {
              errorMessage = "Access forbidden - please contact support";
            } else if (status >= 500) {
              errorMessage = "Server error - please try again later";
            }
          }

          // Check for error message
          if ("message" in errorObj && typeof errorObj.message === "string") {
            const msg = errorObj.message.toLowerCase();
            if (msg.includes("cors")) {
              errorMessage = "Configuration error - please contact support";
            } else if (msg.includes("network")) {
              errorMessage = "Network error - please check your connection";
            }
          }
        }

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [target]
  );

  const handleSubmitClick = useCallback(() => setError(null), [setError]);

  return (
    <Layout title="Login" publicLayout>
      <Formik
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={loginValidationSchema}
        initialValues={{ username: "", password: "" }}
        onSubmit={handleSubmit}
      >
        <Form>
          {error && (
            <ErrorPanel data-testid="login-error">
              <Error>{error}</Error>
            </ErrorPanel>
          )}
          <InputField name="username">Email</InputField>
          <InputField name="password" type="password">
            Password
          </InputField>
          <LoginMetaGrid>
            <Link to="/login/recover">Recover password</Link>
            <Button type="submit" onClick={handleSubmitClick} disabled={isLoading}>
              {isLoading ? "Logging in..." : "Login"}
            </Button>
            <p>Contact Entia for more information about the Liberty service.</p>
          </LoginMetaGrid>
        </Form>
      </Formik>
    </Layout>
  );
};
